import { useCallback, useEffect, useMemo } from 'react'

import { handleException } from 'clients/exception-handling'
import {
  buildProjectCatalogPathFromLens,
  getCatalogCollectionTypeFromLens,
  getCatalogLensFromCollectionType,
  getCatalogRedirectFromLegacyQuery,
} from 'features/catalog/utils'

const safeDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value)
  } catch (cause) {
    handleException(cause, {
      operation: 'catalog.url.decode',
      disposition: 'expected',
      isExpectedError: (cause) => cause instanceof URIError,
      reason: 'Malformed URL encoding is displayed as its original text.',
    })

    return value
  }
}

interface NavigateOptions {
  replace?: boolean
}

type NavigateFn = (to: string, options?: NavigateOptions) => void

type SetStringState = (
  value: string | ((prev: string | null) => string | null) | null,
) => void

export interface ParsedCatalogUrlState {
  lensFromPath: string | null
  lensFromQuery: string | null
  focusFromPath: string | null
  focusFromQuery: string | null
  subthemeFromPath: string | null
  subthemeFromQuery: string | null
  selectedLensFromUrl: string
  selectedFocusFromUrl: string | null
  selectedSubthemeFromUrl: string | null
  isInvalidPathCollection: boolean
  isInvalidLegacyCategory: boolean
  isInvalidPathSubtheme: boolean
}

interface CatalogUrlNavigation {
  updateLens: (lens: string) => void
  updateFocus: (focus: string | null) => void
  updateSubtheme: (subtheme: string | null) => void
}

export const useParsedCatalogUrlState = (
  collectionType: string | undefined,
  itemName: string | undefined,
  subthemeName: string | undefined,
  search: string,
): ParsedCatalogUrlState => {
  return useMemo(() => {
    const urlParams = new URLSearchParams(search)
    const legacyCategory = urlParams.get('category')
    const legacyItem = urlParams.get('item')
    const legacySubtheme = urlParams.get('subtheme')

    const lensFromPath = getCatalogLensFromCollectionType(
      collectionType ?? null,
    )
    const lensFromQuery = getCatalogLensFromCollectionType(legacyCategory)
    const focusFromPath = itemName ?? null
    const focusFromQuery = legacyItem
    const subthemeFromPath = subthemeName ?? null
    const subthemeFromQuery = legacySubtheme

    return {
      lensFromPath,
      lensFromQuery,
      focusFromPath,
      focusFromQuery,
      subthemeFromPath,
      subthemeFromQuery,
      selectedLensFromUrl: lensFromPath ?? lensFromQuery ?? 'product_hierarchy',
      selectedFocusFromUrl: focusFromPath ?? focusFromQuery ?? null,
      selectedSubthemeFromUrl: subthemeFromPath ?? subthemeFromQuery ?? null,
      isInvalidPathCollection: Boolean(collectionType) && !lensFromPath,
      isInvalidLegacyCategory:
        !collectionType && Boolean(legacyCategory) && !lensFromQuery,
      isInvalidPathSubtheme: false,
    }
  }, [collectionType, itemName, search, subthemeName])
}

export const useCatalogUrlNavigation = (
  projectId: string | null | undefined,
  selectedLens: string | null,
  selectedFocus: string | null,
  search: string,
  navigate: NavigateFn,
): CatalogUrlNavigation => {
  const getRemainingParams = useCallback(() => {
    const urlParams = new URLSearchParams(search)
    urlParams.delete('category')
    urlParams.delete('item')
    urlParams.delete('subtheme')
    return urlParams.toString()
  }, [search])

  const updateLens = useCallback(
    (lens: string) => {
      if (!projectId) {
        return
      }

      const remainingParams = getRemainingParams()
      const nextPath = buildProjectCatalogPathFromLens(projectId, lens)
      navigate(`${nextPath}${remainingParams ? `?${remainingParams}` : ''}`)
    },
    [getRemainingParams, navigate, projectId],
  )

  const updateFocus = useCallback(
    (focus: string | null) => {
      if (!projectId || !selectedLens) {
        return
      }

      const remainingParams = getRemainingParams()
      const nextPath = buildProjectCatalogPathFromLens(
        projectId,
        selectedLens,
        focus,
      )
      navigate(`${nextPath}${remainingParams ? `?${remainingParams}` : ''}`)
    },
    [getRemainingParams, navigate, projectId, selectedLens],
  )

  const updateSubtheme = useCallback(
    (subtheme: string | null) => {
      if (!projectId || selectedLens !== 'theme' || !selectedFocus) {
        return
      }

      const remainingParams = getRemainingParams()
      const nextPath = buildProjectCatalogPathFromLens(
        projectId,
        selectedLens,
        selectedFocus,
        subtheme,
      )
      navigate(`${nextPath}${remainingParams ? `?${remainingParams}` : ''}`)
    },
    [getRemainingParams, navigate, projectId, selectedFocus, selectedLens],
  )

  return { updateLens, updateFocus, updateSubtheme }
}

export const useCatalogRouteSync = ({
  projectId,
  collectionType,
  itemName,
  subthemeName,
  search,
  navigate,
  parsedUrlState,
  setSelectedLens,
  setSelectedFocus,
}: {
  projectId: string | null | undefined
  collectionType: string | undefined
  itemName: string | undefined
  subthemeName: string | undefined
  search: string
  navigate: NavigateFn
  parsedUrlState: ParsedCatalogUrlState
  setSelectedLens: SetStringState
  setSelectedFocus: SetStringState
}): void => {
  useEffect(() => {
    if (!projectId) {
      return
    }

    if (
      parsedUrlState.isInvalidPathCollection ||
      parsedUrlState.isInvalidLegacyCategory ||
      parsedUrlState.isInvalidPathSubtheme
    ) {
      navigate(`/project/${projectId}/not-found`, { replace: true })
      return
    }

    const redirectUrl = getCatalogRedirectFromLegacyQuery(
      projectId,
      collectionType,
      search,
      itemName,
      subthemeName,
    )
    if (redirectUrl) {
      navigate(redirectUrl, { replace: true })
      return
    }

    if (collectionType && parsedUrlState.lensFromPath) {
      const canonicalCollectionType = getCatalogCollectionTypeFromLens(
        parsedUrlState.lensFromPath,
      )
      const normalizedCollectionType =
        safeDecodeURIComponent(collectionType).toLowerCase()
      if (normalizedCollectionType !== canonicalCollectionType) {
        const nextPath = buildProjectCatalogPathFromLens(
          projectId,
          parsedUrlState.lensFromPath,
          itemName,
          subthemeName,
        )
        navigate(`${nextPath}${search}`, { replace: true })
        return
      }
    }

    setSelectedLens(parsedUrlState.selectedLensFromUrl)
    setSelectedFocus(parsedUrlState.selectedFocusFromUrl)
  }, [
    collectionType,
    itemName,
    navigate,
    parsedUrlState,
    projectId,
    search,
    subthemeName,
    setSelectedFocus,
    setSelectedLens,
  ])
}
